// src/redux/userSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserLite, UsersSummary } from '../types/response/UserLite';
import { RootState } from '../../../redux/store';

type PageIndex = number;

export interface CursorPage<T> {
	items: T[];
}

export interface UsersState {
	page: number;
	rowsPerPage: number;
	cursors: Array<number | null>;
	pageCache: Record<PageIndex, CursorPage<UserLite>>;
	nextCursorByPage: Record<PageIndex, number | null>;
	virtualCount: number;
	summary?: UsersSummary | null;
}

const initialState: UsersState = {
	page: 0,
	rowsPerPage: 50,
	cursors: [null],
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	summary: null,
};

const usersSlice = createSlice({
	name: 'users',
	initialState,
	reducers: {
		resetUsersState: () => initialState,

		resetUsersCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setUsersRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		setUsersPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		setUsersPageData(
			state,
			action: PayloadAction<{ page: number; items: UserLite[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;
			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;
			state.cursors[page + 1] = nextCursor ?? null;

			const maxLoaded = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoaded] != null;
			state.virtualCount = (maxLoaded + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		setUsersSummary(state, action: PayloadAction<UsersSummary | null | undefined>) {
			state.summary = action.payload ?? null;
		},
	},
});

export const {
	resetUsersState,
	resetUsersCursorPaging,
	setUsersRowsPerPage,
	setUsersPage,
	setUsersPageData,
	setUsersSummary,
} = usersSlice.actions;

export default usersSlice.reducer;

// selectors
export const selectUsersPage = (s: RootState) => s.users.page;
export const selectUsersRowsPerPage = (s: RootState) => s.users.rowsPerPage;
export const selectUsersSummary = (s: RootState) => s.users.summary;

export const selectUsersItems = (s: RootState) => {
	const p = s.users.page;
	return s.users.pageCache[p]?.items ?? [];
};
export const selectUsersCount = (s: RootState) => s.users.virtualCount;

export const selectUsersCursorForPage = (p: number) => (s: RootState) => s.users.cursors[p] ?? null;

export const selectUsersHasCachedPage = (p: number) => (s: RootState) =>
	Boolean(s.users.pageCache[p]);
